import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import {
  CATALOG_PAGE_SIZE,
  appendUniqueById,
  asArray,
  readPagination,
  unwrapData,
} from "../lib/catalog";
import { CatalogSkeletonGrid } from "../components/catalog/CatalogSkeletons";
import PackageCard from "../components/packages/PackageCard";
import PackageFilterBar, {
  countPackageFilters,
  emptyPackageFilters,
  packageMatchesFilters,
} from "../components/packages/PackageFilterOverlay";

function getPackageImage(pkg) {
  if (pkg?.image) return pkg.image;
  if (pkg?.u_pkg_image) return pkg.u_pkg_image;
  return "";
}

function normalizeStatus(value) {
  const raw = String(value ?? "").trim();
  const key = raw.toLowerCase().replace(/[_-]+/g, " ");

  if (!raw) return "Available";

  if (
    [
      "1",
      "yes",
      "y",
      "enable",
      "enabled",
      "true",
      "active",
      "available",
    ].includes(key)
  ) {
    return "Available";
  }

  if (
    [
      "0",
      "no",
      "n",
      "disable",
      "disabled",
      "false",
      "inactive",
      "unavailable",
      "not available",
    ].includes(key)
  ) {
    return "Not Available";
  }

  return raw;
}

/**
 * Returns true when the package departure date has already passed.
 *
 * Handles values such as:
 * 2026-08-31
 * 2026-08-31T00:00:00.000Z
 */
function isPackageExpired(departureDate) {
  if (!departureDate) return false;

  const date = new Date(departureDate);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const today = new Date();

  // Compare only calendar dates in UTC.
  const departureDay = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );

  const todayDay = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  return departureDay < todayDay;
}

function normalizePackages(payload) {
  const raw = asArray(unwrapData(payload));

  return raw.map((item) => ({
    id: item.id || item.u_pkg_id,
    code: item.code || item.u_pkg_code,
    name: item.name || item.u_pkg_tour_name,
    slug: item.slug || item.u_pkg_tour_name_slugs,
    image: getPackageImage(item),
    departureDate: item.departureDate || item.u_pkg_dep_date,
    departureCityId: item.departureCityId || item.u_pkg_dep_city,
    duration: item.duration || item.u_pkg_duration || item.u_pkg_days,
    nights: item.nights || item.u_pkg_nights,
    approxPrice: item.approxPrice || item.u_pkg_approx_price,
    netPrice: item.netPrice,
    rsp: item.rsp,
    seats:
      item.configuredCapacity ??
      item.seats ??
      item.u_pkg_seats,
    availableSeats:
      item.availableSeats ??
      item.available_seats,
    bookedSeats:
      item.bookedSeats ??
      item.booked_seats,
    configuredCapacity:
      item.configuredCapacity ??
      item.seats ??
      item.u_pkg_seats,
    soldOut:
      Boolean(item.soldOut) ||
      Number(item.availableSeats ?? item.available_seats) === 0,
    type:
      item.tourType ||
      item.pkg_tour_category ||
      item.u_pkg_tour_type ||
      item.category,
    status:
      Number(item.availableSeats ?? item.available_seats) === 0
        ? "Sold Out"
        : normalizeStatus(
            item.status ||
              item.u_pkg_enable ||
              "Available"
          ),
    hotels: Array.isArray(item.hotels) ? item.hotels : [],
  }));
}

export default function PackagesPage() {
  const [searchParams] = useSearchParams();

  const initialQuery = searchParams.get("q") || "";

  const [packages, setPackages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(initialQuery);
  const [appliedSearch, setAppliedSearch] = useState(initialQuery);
  const [appliedFilters, setAppliedFilters] = useState(
    emptyPackageFilters
  );

  useEffect(() => {
    let alive = true;

    async function loadPackages() {
      setLoading(true);

      try {
        const payload = await agentApi.get("/umrah-packages", {
          params: {
            page: 1,
            limit: CATALOG_PAGE_SIZE,
            search: appliedSearch.trim() || undefined,
            audience: "agent",
          },
        });

        if (!alive) return;

        const items = normalizePackages(payload);
        const pagination = readPagination(payload);

        setPackages(items);
        setPage(1);
        setHasNext(pagination.hasNext);
        setTotal(pagination.total || items.length);
      } catch (fetchError) {
        if (!alive) return;

        setPackages([]);
        setHasNext(false);

        showApiError(fetchError, "LOAD_PACKAGES");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadPackages();

    return () => {
      alive = false;
    };
  }, [appliedSearch]);

  useEffect(() => {
    const nextQuery = searchParams.get("q") || "";

    setSearch(nextQuery);
    setAppliedSearch(nextQuery);
  }, [searchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearch((current) => {
        const next = search.trim();

        return current === next ? current : next;
      });
    }, 400);

    return () => window.clearTimeout(handle);
  }, [search]);

  async function loadMore() {
    const nextPage = page + 1;

    setLoadingMore(true);

    try {
      const payload = await agentApi.get("/umrah-packages", {
        params: {
          page: nextPage,
          limit: CATALOG_PAGE_SIZE,
          search: appliedSearch.trim() || undefined,
          audience: "agent",
        },
      });

      const items = normalizePackages(payload);
      const pagination = readPagination(payload);

      setPackages((current) =>
        appendUniqueById(
          current,
          items,
          (pkg) => pkg.id
        )
      );

      setPage(nextPage);
      setHasNext(pagination.hasNext);
      setTotal(pagination.total || 0);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_PACKAGES");
    } finally {
      setLoadingMore(false);
    }
  }

  function applySearch() {
    setAppliedSearch(search.trim());
  }

  const displayedPackages = useMemo(
    () =>
      packages.filter((pkg) => {
        // Do not show packages whose departure date has passed.
        if (isPackageExpired(pkg.departureDate)) {
          return false;
        }

        return packageMatchesFilters(
          pkg,
          appliedFilters
        );
      }),
    [packages, appliedFilters]
  );

  const hasActiveFilters =
    countPackageFilters(appliedFilters) > 0;

  return (
    <AgentLayout>
      <div className="container-fluid py-24 package-catalog-page">
        <PageHeader
          title="Umrah Packages"
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Packages" },
          ]}
        >
          <PackageFilterBar
            search={search}
            onSearchChange={setSearch}
            onSearchSubmit={applySearch}
            filters={appliedFilters}
            onFilterChange={setAppliedFilters}
          />
        </PageHeader>

        {loading ? (
          <CatalogSkeletonGrid
            variant="package"
            count={8}
            label="Loading live packages..."
          />
        ) : null}

        {!loading && displayedPackages.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <h5 className="mb-2">
                No packages found
              </h5>

              <p className="text-secondary mb-0">
                {hasActiveFilters
                  ? "Try clearing filters or searching with a different term."
                  : "Try a different search term or refresh after creating a package in admin."}
              </p>
            </div>
          </div>
        ) : null}

        {!loading && displayedPackages.length > 0 ? (
          <div className="package-catalog-grid">
            {displayedPackages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
              />
            ))}
          </div>
        ) : null}

        {!loading && hasNext ? (
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={loadingMore}
              onClick={loadMore}
            >
              {loadingMore
  ? "Loading..."
  : `Load more packages${
      displayedPackages.length
        ? ` (${displayedPackages.length})`
        : ""
    }`}
            </button>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}